import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Dumbbell, CreditCard, CheckSquare, Calendar, User, Phone, MapPin, Target, Share2, FileText, QrCode, Activity, TrendingUp } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useApp } from '../context/AppContext';
import { formatDate, getAge, getStatusBadgeClass, getStatusLabel, daysUntilExpiry, formatPrice, getTodayString } from '../utils/helpers';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, subMonths, addMonths, parseISO, isAfter } from 'date-fns';
import { ko } from 'date-fns/locale';
import Modal from '../components/ui/Modal';
import MemberForm from './MemberForm';
import { getMemberBodyRecords } from '../lib/bodyRecords';
import { getPtSessionsForGym } from '../lib/ptSessions';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const AttendanceCalendar = ({ attendances }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const attendedDates = useMemo(() => {
    return new Set(attendances.map(a => a.date));
  }, [attendances]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad = getDay(monthStart); // 0=일
  const monthLabel = format(currentMonth, 'yyyy년 M월', { locale: ko });

  const monthCount = days.filter(d => attendedDates.has(format(d, 'yyyy-MM-dd'))).length;
  const today = getTodayString();

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCurrentMonth(m => subMonths(m, 1))} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          ‹
        </button>
        <div className="text-center">
          <p className="font-semibold text-gray-900 dark:text-white">{monthLabel}</p>
          <p className="text-xs text-gray-400">{monthCount}회 출석</p>
        </div>
        <button onClick={() => setCurrentMonth(m => addMonths(m, 1))} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          ›
        </button>
      </div>

      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((w, i) => (
          <div key={w} className={`text-center text-xs font-medium py-1 ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'}`}>
            {w}
          </div>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: startPad }, (_, i) => <div key={`pad-${i}`} />)}
        {days.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const isAttended = attendedDates.has(dateStr);
          const isToday = dateStr === today;
          const dow = getDay(day);
          return (
            <div
              key={dateStr}
              className={`aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                isAttended
                  ? 'bg-primary-600 text-white'
                  : isToday
                    ? 'ring-2 ring-primary-400 text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${dow === 0 && !isAttended ? 'text-red-400 dark:text-red-400' : ''} ${dow === 6 && !isAttended ? 'text-blue-400 dark:text-blue-400' : ''}`}
            >
              {format(day, 'd')}
            </div>
          );
        })}
      </div>

      {/* 범례 */}
      <div className="flex gap-4 mt-4 text-xs text-gray-400 justify-center">
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-primary-600 inline-block" />출석</span>
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded ring-2 ring-primary-400 inline-block" />오늘</span>
      </div>

      {/* 최근 출석 목록 */}
      {attendances.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">최근 출석 기록 (총 {attendances.length}회)</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {attendances.slice(0, 10).map(att => (
              <div key={att.id} className="flex justify-between text-sm py-1 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <span className="text-gray-700 dark:text-gray-300">{formatDate(att.date)}</span>
                <span className="text-gray-400">{att.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2">
    <div className="flex items-center gap-2 w-28 flex-shrink-0">
      <Icon className="w-4 h-4 text-gray-400" />
      <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
    </div>
    <span className="text-sm text-gray-900 dark:text-white">{value || '-'}</span>
  </div>
);

const MemberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { members, memberships, attendances, ptAssignments, ptLogs, gymId } = useApp();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [bodyRecords, setBodyRecords] = useState([]);
  const [ptSessions, setPtSessions] = useState([]);

  useEffect(() => {
    if (activeTab === 'body') {
      getMemberBodyRecords(id).then(setBodyRecords);
    }
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab === 'pt' && gymId) {
      getPtSessionsForGym(gymId).then(all => {
        setPtSessions(all.filter(s => s.memberId === id));
      });
    }
  }, [activeTab, gymId, id]);

  const member = members.find(m => m.id === id);
  if (!member) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-400">회원을 찾을 수 없습니다.</p>
        <button onClick={() => navigate('/members')} className="btn-primary mt-4">목록으로</button>
      </div>
    );
  }

  const memberMemberships = memberships.filter(ms => ms.memberId === id);
  const memberAttendances = [...attendances.filter(a => a.memberId === id)].sort((a, b) => b.date.localeCompare(a.date));
  const memberPt = ptAssignments.filter(p => p.memberId === id);
  const memberPtLogs = [...ptLogs.filter(l => l.memberId === id)].sort((a, b) => b.date.localeCompare(a.date));

  const activeMembership = memberMemberships.find(ms => ms.status === 'active');
  const expiryDays = activeMembership ? daysUntilExpiry(activeMembership?.endDate) : null;

  const thisMonthStr = format(new Date(), 'yyyy-MM');
  const thisMonthCount = memberAttendances.filter(a => a.date?.startsWith(thisMonthStr)).length;
  const activePtRemaining = memberPt.filter(p => p.status === 'active').reduce((sum, p) => sum + (p.totalSessions - p.usedSessions), 0);
  const joinDays = member.createdAt ? Math.floor((Date.now() - new Date(member.createdAt).getTime()) / 86400000) : null;

  const tabs = [
    { id: 'info', label: '기본정보', icon: User },
    { id: 'membership', label: `회원권 (${memberMemberships.length})`, icon: CreditCard },
    { id: 'attendance', label: `출석 (${memberAttendances.length})`, icon: CheckSquare },
    { id: 'pt', label: `PT (${memberPt.length})`, icon: Dumbbell },
    { id: 'body', label: '신체기록', icon: Activity },
    { id: 'qr', label: 'QR 회원증', icon: QrCode },
  ];

  return (
    <div className="space-y-4 max-w-4xl">
      {/* 헤더 */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/members')}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center overflow-hidden">
            {member.photo ? (
              <img src={member.photo} alt={member.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-xl font-bold text-primary-700 dark:text-primary-400">{member.name[0]}</span>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{member.name}</h2>
              <span className={getStatusBadgeClass(member.status)}>{getStatusLabel(member.status)}</span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {member.gender === 'male' ? '남성' : '여성'}{getAge(member.birthDate) ? ` · ${getAge(member.birthDate)}세` : ''} · {member.phone}
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsEditOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Edit className="w-4 h-4" />
          수정
        </button>
      </div>

      {/* 이용 현황 배너 */}
      {activeMembership && (
        <div className={`rounded-xl p-4 flex items-center justify-between ${
          expiryDays !== null && expiryDays <= 3 ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
          expiryDays !== null && expiryDays <= 7 ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' :
          'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
        }`}>
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{activeMembership.productName}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {formatDate(activeMembership.startDate)} ~ {formatDate(activeMembership.endDate)}
            </p>
          </div>
          <div className="text-right">
            {expiryDays !== null && expiryDays >= 0 ? (
              <p className={`text-2xl font-bold ${
                expiryDays <= 3 ? 'text-red-600 dark:text-red-400' :
                expiryDays <= 7 ? 'text-yellow-600 dark:text-yellow-400' :
                'text-green-600 dark:text-green-400'
              }`}>D-{expiryDays}</p>
            ) : (
              <p className="text-sm font-semibold text-red-600 dark:text-red-400">만료됨</p>
            )}
          </div>
        </div>
      )}

      {/* 요약 스탯 카드 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{memberAttendances.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">총 출석</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{thisMonthCount}</p>
          <p className="text-xs text-gray-400 mt-0.5">이번달 출석</p>
        </div>
        <div className="card p-4 text-center">
          <p className={`text-2xl font-bold ${activePtRemaining <= 3 ? 'text-red-500' : activePtRemaining <= 10 ? 'text-yellow-500' : 'text-green-600 dark:text-green-400'}`}>
            {activePtRemaining}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">PT 잔여</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{joinDays ?? '-'}</p>
          <p className="text-xs text-gray-400 mt-0.5">가입 일수</p>
        </div>
      </div>

      {/* 탭 */}
      <div className="card overflow-hidden">
        <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {/* 기본 정보 탭 */}
          {activeTab === 'info' && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              <InfoRow icon={User} label="이름" value={member.name} />
              <InfoRow icon={User} label="성별" value={member.gender === 'male' ? '남성' : '여성'} />
              <InfoRow icon={Calendar} label="생년월일" value={member.birthDate ? `${formatDate(member.birthDate)} (만 ${getAge(member.birthDate)}세)` : null} />
              <InfoRow icon={Phone} label="전화번호" value={member.phone} />
              <InfoRow icon={MapPin} label="주소" value={member.address} />
              <InfoRow icon={Target} label="운동목적" value={member.purpose} />
              <InfoRow icon={Share2} label="알게된경로" value={member.referral} />
              <InfoRow icon={Calendar} label="등록일" value={formatDate(member.createdAt)} />
              {member.memo && (
                <div className="flex items-start gap-3 py-2">
                  <div className="flex items-center gap-2 w-28 flex-shrink-0">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">메모</span>
                  </div>
                  <span className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{member.memo}</span>
                </div>
              )}
            </div>
          )}

          {/* 회원권 탭 */}
          {activeTab === 'membership' && (
            <div className="space-y-3">
              {memberMemberships.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">보유 회원권이 없습니다.</p>
              ) : (
                memberMemberships.map(ms => {
                  const days = daysUntilExpiry(ms.endDate);
                  const isExpired = ms.status === 'expired' || (days !== null && days < 0);
                  return (
                    <div key={ms.id} className={`p-4 rounded-xl border ${isExpired ? 'border-gray-200 dark:border-gray-700 opacity-60' : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{ms.productName}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {formatDate(ms.startDate)} ~ {formatDate(ms.endDate)}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{formatPrice(ms.price)}</p>
                        </div>
                        <div className="text-right">
                          {isExpired ? (
                            <span className="badge-red">만료</span>
                          ) : (
                            <span className="badge-green">이용중 {days !== null ? `(D-${days})` : ''}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* 출석 탭 - 달력 뷰 */}
          {activeTab === 'attendance' && (
            <AttendanceCalendar attendances={memberAttendances} />
          )}

          {/* 신체기록 탭 */}
          {activeTab === 'body' && (
            <div className="space-y-3">
              {bodyRecords.length === 0 ? (
                <div className="text-center py-10">
                  <Activity className="w-10 h-10 text-gray-200 dark:text-gray-700 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">기록된 신체 정보가 없습니다.</p>
                </div>
              ) : (
                <>
                  {/* 최신 기록 요약 */}
                  {(() => {
                    const latest = bodyRecords[0];
                    const prev = bodyRecords[1];
                    return (
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                        <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2">
                          최근 기록 · {latest.date ? format(parseISO(latest.date), 'yyyy.MM.dd') : ''}
                        </p>
                        <div className="flex gap-6">
                          {latest.weight != null && (
                            <div>
                              <p className="text-xs text-gray-400">체중</p>
                              <p className="text-xl font-bold text-gray-900 dark:text-white">{latest.weight}<span className="text-sm font-normal">kg</span></p>
                              {prev?.weight != null && (
                                <p className={`text-xs flex items-center gap-0.5 ${latest.weight < prev.weight ? 'text-blue-500' : latest.weight > prev.weight ? 'text-red-400' : 'text-gray-400'}`}>
                                  <TrendingUp className="w-3 h-3" />
                                  {latest.weight > prev.weight ? '+' : ''}{(latest.weight - prev.weight).toFixed(1)}kg
                                </p>
                              )}
                            </div>
                          )}
                          {latest.bodyFat != null && (
                            <div>
                              <p className="text-xs text-gray-400">체지방</p>
                              <p className="text-xl font-bold text-gray-900 dark:text-white">{latest.bodyFat}<span className="text-sm font-normal">%</span></p>
                            </div>
                          )}
                          {latest.muscleMass != null && (
                            <div>
                              <p className="text-xs text-gray-400">근육량</p>
                              <p className="text-xl font-bold text-gray-900 dark:text-white">{latest.muscleMass}<span className="text-sm font-normal">kg</span></p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* 전체 기록 목록 */}
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">전체 기록 ({bodyRecords.length})</p>
                  <div className="space-y-2">
                    {bodyRecords.map(rec => (
                      <div key={rec.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                        <span className="text-sm text-gray-500 dark:text-gray-400 w-28 flex-shrink-0">
                          {rec.date ? format(parseISO(rec.date), 'yyyy.MM.dd (EEE)', { locale: ko }) : ''}
                        </span>
                        <div className="flex gap-4 text-sm">
                          {rec.weight != null && <span className="text-gray-700 dark:text-gray-300"><span className="text-gray-400 text-xs">체중</span> <span className="font-semibold">{rec.weight}kg</span></span>}
                          {rec.bodyFat != null && <span className="text-gray-700 dark:text-gray-300"><span className="text-gray-400 text-xs">체지방</span> <span className="font-semibold">{rec.bodyFat}%</span></span>}
                          {rec.muscleMass != null && <span className="text-gray-700 dark:text-gray-300"><span className="text-gray-400 text-xs">근육량</span> <span className="font-semibold">{rec.muscleMass}kg</span></span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* QR 회원증 탭 */}
          {activeTab === 'qr' && (
            <div className="flex flex-col items-center gap-6 py-4">
              <div className="bg-white p-6 rounded-2xl shadow-lg flex flex-col items-center gap-4 w-64">
                <QRCodeSVG
                  value={`gym-member:${member.id}`}
                  size={192}
                  level="M"
                  includeMargin={false}
                />
                <div className="text-center">
                  <p className="font-bold text-gray-900 text-lg">{member.name}</p>
                  <p className="text-sm text-gray-500">{member.phone}</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 text-center max-w-xs">
                이 QR코드를 키오스크에 스캔하면 자동으로 출석 체크됩니다.
              </p>
            </div>
          )}

          {/* PT 탭 */}
          {activeTab === 'pt' && (
            <div className="space-y-4">
              {/* 예정 세션 */}
              {ptSessions.filter(s => s.status === 'scheduled' && isAfter(parseISO(s.scheduledAt), new Date())).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">예정 일정</p>
                  <div className="space-y-2">
                    {ptSessions
                      .filter(s => s.status === 'scheduled' && isAfter(parseISO(s.scheduledAt), new Date()))
                      .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
                      .map(s => (
                        <div key={s.id} className="flex items-center justify-between px-4 py-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{s.trainerName} 트레이너</p>
                            <p className="text-xs text-gray-400">{s.productName}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-blue-600 dark:text-blue-400">
                              {format(parseISO(s.scheduledAt), 'M/d (EEE)', { locale: ko })}
                            </p>
                            <p className="text-xs text-gray-400">{format(parseISO(s.scheduledAt), 'HH:mm')}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {memberPt.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">PT 이력이 없습니다.</p>
              ) : (
                memberPt.map(pt => {
                  const remaining = pt.totalSessions - pt.usedSessions;
                  const progress = (pt.usedSessions / pt.totalSessions) * 100;
                  const logs = memberPtLogs.filter(l => l.ptAssignmentId === pt.id);
                  return (
                    <div key={pt.id} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{pt.productName}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{pt.trainerName} 트레이너</p>
                        </div>
                        <span className={remaining <= 0 ? 'badge-red' : remaining <= 5 ? 'badge-yellow' : 'badge-green'}>
                          잔여 {remaining}회
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-1">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: `${progress}%` }} />
                      </div>
                      <p className="text-xs text-gray-400">{pt.usedSessions}/{pt.totalSessions}회 수업 완료</p>

                      {logs.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">PT 일지</p>
                          {logs.slice(0, 5).map(log => (
                            <div key={log.id} className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                                <span>{formatDate(log.date)}</span>
                                <span>{log.sessionNumber}회차</span>
                              </div>
                              <p className="text-sm text-gray-700 dark:text-gray-300">{log.content}</p>
                              {log.memo && <p className="text-xs text-gray-400 mt-1">메모: {log.memo}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title="회원 수정" size="lg">
        <MemberForm member={member} onClose={() => setIsEditOpen(false)} />
      </Modal>
    </div>
  );
};

export default MemberDetail;
